import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { compactActionSx, money, surfaceSx } from "../center/ownerCenterUi";
import { recurringFrequencyLabel } from "../center/ownerRecurringUi";

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function minutesLabel(value) {
  const minutes = Math.max(0, Number(value || 0));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function localTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("es-AR", { hour: "2-digit", minute: "2-digit" }).format(date);
}

function dayTitle(date) {
  const value = new Date(`${date}T12:00:00`);
  return new Intl.DateTimeFormat("es-AR", { weekday: "long", day: "numeric", month: "long" }).format(value);
}

function economyTone(row) {
  if (Number(row.expectedExpense || 0) <= 0) return "Sin compromisos recurrentes suficientes para medir cobertura.";
  if (Number(row.expectedNet || 0) < 0) return `Falta cubrir ${money(Math.abs(row.expectedNet), row.currency)} en los próximos 30 días antes de proyectar excedente.`;
  if (Number(row.expectedNet || 0) === 0) return "Los recurrentes están cubiertos, pero todavía no hay margen recurrente.";
  return `Margen recurrente esperado ${money(row.expectedNet, row.currency)}. Conviene verificar que ese excedente también aparezca en los meses reales.`;
}

const hiddenScrollSx = {
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { width: 0, height: 0, display: "none" },
};

function Metric({ label, value, helper }) {
  return (
    <Box sx={{ minWidth: 0, px: { xs: .2, sm: .45 } }}>
      <Typography noWrap sx={{ color: "text.secondary", fontWeight: 850, fontSize: ".59rem", textTransform: "uppercase", letterSpacing: ".035em" }}>{label}</Typography>
      <Typography noWrap sx={{ fontWeight: 1000, fontSize: { xs: ".92rem", sm: "1.02rem" }, lineHeight: 1.2, mt: .08 }}>{value}</Typography>
      {helper ? <Typography noWrap sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".59rem", mt: .05 }}>{helper}</Typography> : null}
    </Box>
  );
}

function AgendaRow({ item, updating, onComplete, onToggleRoutine }) {
  const id = idOf(item.row);
  const updateKey = `${item.kind}:${id}`;
  const routine = item.kind === "routine";
  const google = item.kind === "google";
  const executable = routine || google || item.row?.executability?.executable !== false;
  const dependencyTitle = item.row?.executability?.dependency?.title || item.row?.dependency?.title || "";
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "44px minmax(0,1fr)", sm: "50px minmax(0,1fr) auto" },
        gap: .65,
        alignItems: "center",
        px: .7,
        py: .55,
        borderRadius: 1.6,
        bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.025)",
        border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.035)",
        "& > .MuiButton-root": { gridColumn: { xs: "1 / -1", sm: "auto" }, justifySelf: { xs: "stretch", sm: "auto" } },
      }}
    >
      <Typography sx={{ fontWeight: 1000, fontSize: ".72rem" }}>{item.at}</Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 900, fontSize: ".79rem", textDecoration: routine && item.row.completed ? "line-through" : "none", opacity: routine && item.row.completed ? .62 : 1 }}>{item.title}</Typography>
        <Typography noWrap sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".63rem" }}>
          {routine ? `Rutina · hasta ${item.end}${item.row.completed ? " · hecha" : ""}` : google ? item.detail : !executable && dependencyTitle ? `Depende de ${dependencyTitle}` : item.detail}
        </Typography>
      </Box>
      {google ? (
        item.row.htmlLink ? <Button size="small" component="a" href={item.row.htmlLink} target="_blank" rel="noreferrer" endIcon={<OpenInNewRoundedIcon />} sx={{ ...compactActionSx, minWidth: 0, py: .3 }}>Google</Button> : <Chip size="small" label="Google" sx={{ height: 23, fontWeight: 850 }} />
      ) : routine ? (
        <Button
          size="small"
          variant={item.row.completed ? "outlined" : "contained"}
          disabled={updating === updateKey}
          startIcon={<CheckCircleRoundedIcon />}
          onClick={() => onToggleRoutine(item.row)}
          sx={{ ...compactActionSx, minWidth: 0, py: .3 }}
        >
          {item.row.completed ? "Desmarcar" : "Hecha"}
        </Button>
      ) : item.row.status === "open" && executable ? (
        <Button size="small" disabled={updating === updateKey} startIcon={<CheckCircleRoundedIcon />} onClick={() => onComplete(item.row)} sx={{ ...compactActionSx, minWidth: 0, py: .3 }}>Hecho</Button>
      ) : item.row.status === "open" ? <Chip size="small" label={dependencyTitle ? "Depende" : "No ejecutable"} sx={{ height: 23, fontWeight: 850 }} /> : <Chip size="small" label="Hecho" sx={{ height: 23, fontWeight: 850 }} />}
    </Box>
  );
}

function PendingRow({ row, updating, onComplete }) {
  const id = idOf(row);
  const executable = row?.executability?.executable !== false;
  const dependencyTitle = row?.executability?.dependency?.title || row?.dependency?.title || "";
  return (
    <Stack direction="row" spacing={.65} alignItems="center" sx={{ py: .55, minWidth: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 900, fontSize: ".78rem" }}>{row.title}</Typography>
        <Typography noWrap sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".62rem" }}>
          {!executable && dependencyTitle ? `Depende de ${dependencyTitle}` : row.calendarReason === "review" ? `Revisar ${localTime(row.calendarAt)}` : row.dueAt ? `Límite ${localTime(row.dueAt)}` : "Sin horario"}
          {row.calendarReason !== "review" ? (row.estimatedMinutes ? ` · est. ${minutesLabel(row.estimatedMinutes)}` : " · sin estimar") : ""}
          {row.actualMinutes ? ` · real ${minutesLabel(row.actualMinutes)}` : ""}
        </Typography>
      </Box>
      {row.status === "open" && executable ? <Button size="small" onClick={() => onComplete(row)} disabled={updating === `commitment:${id}`} sx={{ ...compactActionSx, minWidth: 0, py: .3 }}>Hecho</Button> : row.status === "open" ? <Chip size="small" label={dependencyTitle ? "Depende" : "No ejecutable"} sx={{ height: 23 }} /> : <Chip size="small" label="Hecho" sx={{ height: 23 }} />}
    </Stack>
  );
}

function transactionOriginLabel(row) {
  const kind = String(row?.source?.kind || "manual");
  if (kind === "recurring") return "Recurrente registrado";
  if (kind === "project") return "Proyecto Owner";
  if (kind === "sale") return "Venta";
  if (kind === "import") return "Importación";
  if (kind === "automation") return "Automatización";
  return "Carga manual";
}

function TransactionRow({ row, onMakeRecurring }) {
  const recurring = Boolean(row.recurringPlanId || (row.source?.kind === "recurring" && row.source?.entityId));
  const sign = row.type === "expense" ? "−" : row.type === "income" ? "+" : "";
  const linked = [
    row.initiativeId?.name ? `Proyecto ${row.initiativeId.name}` : "",
    row.projectTermId?.title ? `Contrato ${row.projectTermId.title}` : "",
    row.clientId?.name ? `Cliente ${row.clientId.name}` : "",
    row.accountId?.name ? `Cuenta ${row.accountId.name}` : "",
  ].filter(Boolean);
  return (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={.65} alignItems={{ xs: "stretch", sm: "center" }} sx={{ py: .65, minWidth: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 900, fontSize: ".78rem", lineHeight: 1.3 }}>{row.description || row.category || row.type}</Typography>
        <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".62rem", lineHeight: 1.35 }}>{localTime(row.occurredAt)} · {[row.area, row.category].filter(Boolean).join(" · ") || "Sin categoría"}</Typography>
        <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: ".6rem", lineHeight: 1.35, mt: .12 }}>Origen: {transactionOriginLabel(row)}{linked.length ? ` · ${linked.join(" · ")}` : ""}</Typography>
      </Box>
      <Stack direction="row" spacing={.5} alignItems="center" justifyContent={{ xs: "space-between", sm: "flex-end" }}>
        <Typography sx={{ fontWeight: 1000, whiteSpace: "nowrap", fontSize: ".78rem" }}>{sign}{money(row.amount, row.currency)}</Typography>
        {["expense", "income"].includes(row.type) ? recurring ? <Chip size="small" label="Recurrente" sx={{ height: 23, fontWeight: 900 }} /> : <Button size="small" startIcon={<RepeatRoundedIcon />} onClick={() => onMakeRecurring?.(row)} sx={{ ...compactActionSx, minWidth: 0, py: .3 }}>Recurrente</Button> : null}
      </Stack>
    </Stack>
  );
}

function RecurringRow({ plan, onNavigate }) {
  const occurrence = plan.calendarOccurrence || {};
  const origin = plan.origin || {};
  const isIncome = plan.direction !== "expense";
  const planId = plan.id || plan._id;
  const recurringOriginParam = origin.kind === "infrastructure" ? "&origen=infraestructura" : "";
  const route = origin.route || (origin.projectId
    ? `/dashboard/owner/proyectos/${encodeURIComponent(origin.projectId)}`
    : `/dashboard/owner/finanzas?vista=recurrentes&recurrente=${encodeURIComponent(planId || "")}${recurringOriginParam}`);
  const sourceLabel = origin.label || (origin.projectName ? "Proyecto Owner" : "Recurrente de Finanzas");
  const actionLabel = origin.projectId ? "Ver origen" : "Gestionar recurrente";

  return (
    <Box sx={{ p: .8, borderRadius: 1.8, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.03)", border: "1px solid rgba(var(--wp-panel-ink-rgb,25,36,47),.055)" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={.7} alignItems={{ xs: "stretch", sm: "flex-start" }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={.4} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography sx={{ fontWeight: 950, fontSize: ".78rem", lineHeight: 1.25 }}>{plan.name}</Typography>
            <Chip size="small" color={isIncome ? "success" : "warning"} variant="outlined" label={occurrence.dueState === "overdue" ? (isIncome ? "Cobro atrasado" : "Pago atrasado") : (isIncome ? "Cobrar" : "Pagar")} sx={{ height: 21, fontSize: ".58rem", fontWeight: 900 }} />
            <Chip size="small" variant="outlined" label="Previsto · no realizado" sx={{ height: 21, fontSize: ".56rem", fontWeight: 850 }} />
          </Stack>
          <Typography sx={{ color: occurrence.dueState === "overdue" ? "warning.main" : "text.secondary", fontWeight: 750, fontSize: ".63rem", lineHeight: 1.4, mt: .18 }}>
            {recurringFrequencyLabel(plan.frequency)} · pendiente {money(occurrence.pendingAmount ?? plan.projectedAmount, plan.currency)}{occurrence.paymentStatus === "partial" ? " · pago parcial registrado" : ""}
          </Typography>

          <Box sx={{ mt: .55, px: .65, py: .55, borderRadius: 1.35, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.028)" }}>
            <Stack direction="row" spacing={.4} alignItems="center">
              <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography sx={{ fontWeight: 950, fontSize: ".62rem" }}>Origen: {sourceLabel}</Typography>
            </Stack>
            {origin.projectName ? <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".6rem", mt: .18 }}>Proyecto: {origin.projectName}</Typography> : null}
            {origin.contractTitle ? <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".6rem", mt: .08 }}>Contrato: {origin.contractTitle}</Typography> : null}
            {origin.detail ? <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".6rem", lineHeight: 1.38, mt: .18 }}>{origin.detail}</Typography> : null}
            {origin.dueReason ? <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: ".6rem", lineHeight: 1.38, mt: .18 }}>Por qué aparece hoy: {origin.dueReason}</Typography> : null}
          </Box>
        </Box>
        <Button size="small" variant="outlined" endIcon={<OpenInNewRoundedIcon sx={{ fontSize: 15 }} />} onClick={() => onNavigate(route)} sx={{ ...compactActionSx, minWidth: 0, whiteSpace: "nowrap" }}>{actionLabel}</Button>
      </Stack>
    </Box>
  );
}

function Section({ title, badge, children }) {
  return (
    <Box sx={{ ...surfaceSx, p: { xs: 1.05, sm: 1.15 } }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
        <Typography sx={{ fontWeight: 1000, fontSize: ".83rem" }}>{title}</Typography>
        {badge ? <Chip size="small" label={badge} sx={{ height: 21, fontSize: ".61rem", fontWeight: 900 }} /> : null}
      </Stack>
      <Divider sx={{ my: .65 }} />
      {children}
    </Box>
  );
}

export default function OwnerDaySheet({ calendar, onNewTask, onNewRoutine, onSettings, onNewGoogleEvent, onGoogleSettings, onNewMovement, onMakeRecurring, onChanged }) {
  const navigate = useNavigate();
  const day = calendar?.day || {};
  const routines = day.routines || [];
  const googleEvents = day.googleEvents || [];
  const capacity = day.capacity || {};
  const economy = calendar?.economy?.byCurrency || {};
  const [updating, setUpdating] = React.useState("");
  const [error, setError] = React.useState("");

  const run = async (key, task, fallback) => {
    setUpdating(key);
    setError("");
    try {
      await task();
      await onChanged?.();
    } catch (err) {
      setError(formatApiError(err, fallback));
    } finally {
      setUpdating("");
    }
  };

  const markDone = (commitment) => {
    const id = idOf(commitment);
    return run(`commitment:${id}`, () => ownerApi.updateCommitment(id, { status: "done" }), "No pude completar el pendiente.");
  };

  const toggleRoutine = (routine) => {
    const id = idOf(routine);
    return run(`routine:${id}`, () => ownerApi.setRoutineCheckin(id, calendar.selectedDate, !routine.completed), "No pude actualizar la rutina.");
  };

  const agenda = [
    ...googleEvents.map((row) => ({
      kind: "google",
      at: row.allDay ? "Todo el día" : localTime(row.start),
      sortAt: row.allDay ? "00:00" : localTime(row.start),
      end: row.allDay ? "" : localTime(row.end),
      title: row.title || "(Sin título)",
      detail: row.allDay ? `Google Calendar · todo el día${row.location ? ` · ${row.location}` : ""}` : `Google Calendar${row.end ? ` · hasta ${localTime(row.end)}` : ""}${row.location ? ` · ${row.location}` : ""}`,
      row,
    })),
    ...routines.map((row) => ({ kind: "routine", at: row.startTime, sortAt: row.startTime, end: row.endTime, title: row.name, row })),
    ...(day.commitments || []).filter((row) => row.scheduledStartAt || row.calendarReason === "review").map((row) => {
      const review = row.calendarReason === "review";
      const state = row?.work?.state || "ready";
      const context = state === "waiting" ? row?.work?.waitingFor : state === "blocked" ? (row?.work?.blockedReason || row?.work?.blockedBy) : "";
      return {
        kind: "commitment",
        at: localTime(row.calendarAt || row.scheduledStartAt),
        sortAt: localTime(row.calendarAt || row.scheduledStartAt),
        title: row.title,
        detail: review ? `Revisar${context ? ` · ${context}` : ""}` : `${row.estimatedMinutes ? `est. ${minutesLabel(row.estimatedMinutes)}` : "sin estimar"}${row.actualMinutes ? ` · real ${minutesLabel(row.actualMinutes)}` : ""}`,
        row,
      };
    }),
  ].sort((a, b) => String(a.sortAt || a.at).localeCompare(String(b.sortAt || b.at)));

  const unscheduled = (day.commitments || []).filter((row) => !row.scheduledStartAt && row.calendarReason !== "review");
  const completedRoutines = routines.filter((row) => row.completed).length;
  const openCommitments = (day.commitments || []).filter((row) => row.status === "open").length;

  const actionButton = (label, icon, onClick, primary = false) => (
    <Tooltip title={label} arrow>
      <IconButton
        aria-label={label}
        size="small"
        onClick={onClick}
        sx={{
          width: 31,
          height: 31,
          borderRadius: 1.7,
          color: primary ? "primary.contrastText" : "text.secondary",
          bgcolor: primary ? "primary.main" : "rgba(var(--wp-panel-ink-rgb,25,36,47),.045)",
          border: "1px solid",
          borderColor: primary ? "primary.main" : "divider",
          "&:hover": { bgcolor: primary ? "primary.dark" : "action.hover", color: primary ? "primary.contrastText" : "text.primary" },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        height: { xs: "auto", lg: "100%" },
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: .65,
        overflow: { xs: "visible", lg: "hidden" },
      }}
    >
      <Box sx={{ ...surfaceSx, flex: "0 0 auto", overflow: "hidden" }}>
        <Box sx={{ px: { xs: 1, sm: 1.2 }, py: { xs: .8, sm: .9 }, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.022)" }}>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ textTransform: "capitalize", fontWeight: 1000, fontSize: { xs: ".96rem", sm: "1.08rem" }, letterSpacing: "-.018em" }}>{dayTitle(calendar.selectedDate)}</Typography>
              <Typography noWrap sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".63rem", mt: .05 }}>{agenda.length} bloques · {googleEvents.length} Google · {openCommitments} pendientes · {completedRoutines}/{routines.length} rutinas</Typography>
            </Box>
            <Stack direction="row" spacing={.35} flex="0 0 auto">
              {actionButton("Nueva tarea", <AddTaskRoundedIcon fontSize="small" />, onNewTask, true)}
              {actionButton("Nueva rutina", <RepeatRoundedIcon fontSize="small" />, onNewRoutine)}
              {calendar?.googleCalendar?.connected
                ? actionButton("Nuevo evento Google", <CalendarMonthRoundedIcon fontSize="small" />, onNewGoogleEvent)
                : actionButton("Conectar Google Calendar", <CalendarMonthRoundedIcon fontSize="small" />, onGoogleSettings)}
              {actionButton("Registrar movimiento", <AccountBalanceWalletRoundedIcon fontSize="small" />, onNewMovement)}
              {actionButton("Configurar jornada", <SettingsRoundedIcon fontSize="small" />, onSettings)}
            </Stack>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(4,minmax(0,1fr))" },
              gap: { xs: .45, sm: .75 },
              mt: .75,
              pt: .7,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Metric label="Libre hoy" value={minutesLabel(capacity.freeMinutes)} helper={`${minutesLabel(capacity.availableMinutes)} disponibles`} />
            <Metric label="Agendado" value={minutesLabel(Number(capacity.scheduledMinutes || 0) + Number(capacity.googleBusyMinutes || 0))} helper={capacity.googleBusyMinutes ? `${minutesLabel(capacity.googleBusyMinutes)} Google · ${completedRoutines}/${routines.length} rutinas` : `${completedRoutines}/${routines.length} rutinas`} />
            <Metric label="Pendientes" value={openCommitments} helper={capacity.unsizedTasks ? `${capacity.unsizedTasks} sin duración` : "medidos"} />
            <Metric label="Libre semana" value={minutesLabel(calendar.week?.freeMinutes)} helper={`${minutesLabel(calendar.week?.availableMinutes)} base`} />
          </Box>
        </Box>
      </Box>

      {error ? <Alert severity="error" sx={{ flex: "0 0 auto", py: .2 }}>{error}</Alert> : null}

      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "minmax(0,1.28fr) minmax(270px,.72fr)" },
          gap: .65,
          overflow: { xs: "visible", lg: "hidden" },
        }}
      >
        <Stack
          spacing={.65}
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflowY: { xs: "visible", lg: "auto" },
            overscrollBehavior: "contain",
            pr: { lg: .1 },
            ...hiddenScrollSx,
          }}
        >
          <Section title="Agenda del día" badge={`${agenda.length} bloques`}>
            {agenda.length ? (
              <Stack spacing={.42}>{agenda.map((item, index) => <AgendaRow key={`${item.kind}-${idOf(item.row)}-${index}`} item={item} updating={updating} onComplete={markDone} onToggleRoutine={toggleRoutine} />)}</Stack>
            ) : <Typography sx={{ py: 1.25, color: "text.secondary", fontWeight: 700, fontSize: ".75rem" }}>No hay bloques horarios todavía. Agendá una tarea o rutina para ocupar el día.</Typography>}
          </Section>

          <Section title="Pendientes sin horario" badge={unscheduled.length || null}>
            {unscheduled.length ? <Stack divider={<Divider flexItem />}>{unscheduled.map((row) => <PendingRow key={idOf(row)} row={row} updating={updating} onComplete={markDone} />)}</Stack> : <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".73rem" }}>Sin pendientes sueltos para este día.</Typography>}
          </Section>

          <Section title="Dinero del día" badge={(day.transactions || []).length || null}>
            {(day.transactions || []).length ? <Stack divider={<Divider flexItem />}>{day.transactions.map((row) => <TransactionRow key={idOf(row)} row={row} onMakeRecurring={onMakeRecurring} />)}</Stack> : <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".73rem" }}>No hubo movimientos registrados este día.</Typography>}
          </Section>
        </Stack>

        <Stack
          spacing={.65}
          sx={{
            minWidth: 0,
            minHeight: 0,
            overflowY: { xs: "visible", lg: "auto" },
            overscrollBehavior: "contain",
            ...hiddenScrollSx,
          }}
        >
          <Section title="Cobros y pagos previstos" badge={(day.recurring || []).length || null}>
            {(day.recurring || []).length ? (
              <Stack spacing={.55}>{day.recurring.map((plan) => (
                <RecurringRow key={`${plan.id || plan._id}:${plan.calendarOccurrence?.periodKey || "period"}`} plan={plan} onNavigate={navigate} />
              ))}</Stack>
            ) : <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".73rem" }}>Sin cobros ni pagos recurrentes previstos para este día.</Typography>}
            {(day.sales || []).length ? (
              <Box sx={{ mt: .75 }}>
                <Typography sx={{ fontWeight: 950, fontSize: ".69rem", mb: .25 }}>Trabajos esperados</Typography>
                {day.sales.map((sale) => <Typography key={sale.id || sale._id} noWrap sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".64rem", mt: .2 }}>{sale.title} · {money(sale.total, sale.currency)}</Typography>)}
              </Box>
            ) : null}
          </Section>

          <Section title="Salud financiera">
            {Object.entries(economy).length ? (
              <Stack spacing={.65}>{Object.entries(economy).map(([currency, raw]) => {
                const row = { ...raw, currency };
                return (
                  <Box key={currency}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography sx={{ fontWeight: 950, fontSize: ".74rem" }}>{currency}</Typography>
                      <Typography sx={{ fontWeight: 1000, fontSize: ".78rem" }}>{money(row.expectedNet, currency)}</Typography>
                    </Stack>
                    <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".61rem", mt: .1 }}>30 días: +{money(row.expectedIncome, currency)} · −{money(row.expectedExpense, currency)}</Typography>
                    {row.recentSampleMonths ? <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".61rem" }}>Promedio neto real: {money(row.recentMonthlyNetAverage, currency)} · {row.recentSampleMonths} meses</Typography> : null}
                    <Typography sx={{ mt: .32, fontWeight: 780, fontSize: ".64rem", lineHeight: 1.35 }}>{economyTone(row)}</Typography>
                  </Box>
                );
              })}</Stack>
            ) : <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".73rem" }}>Vinculá pagos reales a acuerdos recurrentes para empezar a medir cobertura.</Typography>}
          </Section>
        </Stack>
      </Box>
    </Box>
  );
}
